import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import type { ActivePaymentMethod } from "../../../../../api/paymentMethods";
import { parseMoneyToCents } from "../../../../../utils/money";
import type { Line } from "./invoiceLines";

/**
 * Фактура за депозит („Депозити" → „Фактуриране"): след готовия бон на
 * депозита (Бон 1, или метод без фискализиране) `InvoiceCreateModal` се отваря
 * с един ред „Авансово плащане (капаро)" за цялата сума и с метода на
 * депозита. Издава се с `POST /invoices` (`bill_ids: []`, `deposit_id`) — БЕЗ
 * нов касов бон: бонът на капарото вече е излязъл при вземането, а BE пише
 * номера му в бележката. Редът и методът са само попълнени — сменят се като
 * във всяка фактура.
 */
export interface DepositInvoiceSource {
  id: number;
  /** Сумата на депозита — JSON низ, като всички пари от BE. */
  amount: string;
  paymentMethodId: number;
  /** „За дата" (YYYY-MM-DD) — за коя дата е авансът; null — безсрочен. */
  validUntil: string | null;
  /** ДДС ставката на аванса от BE (`Deposit.vatRate`): 20, а при фирма без ДДС
   *  регистрация 0. Липсва (по-старо BE) — `DEPOSIT_INVOICE_VAT_RATE`. */
  vatRate?: number;
}

/** Авансът е с данъчната група на „ЧАСТИЧНИ ПЛАЩАНИЯ" на бона — при
 *  регистрирана по ДДС фирма Б, 20 % (ресторантьорска услуга от 01.01.2025).
 *  Само когато BE не казва ставката (`vatRate`). */
const DEPOSIT_INVOICE_VAT_RATE = 20;

/** „Авансово плащане (капаро) за 28.10.2026 — депозит № 17"; без „За дата" —
 *  без датата. */
export const depositInvoiceLineName = (deposit: DepositInvoiceSource): string =>
  deposit.validUntil
    ? `Авансово плащане (капаро) за ${dayjs(deposit.validUntil).format("DD.MM.YYYY")} — депозит № ${deposit.id}`
    : `Авансово плащане (капаро) — депозит № ${deposit.id}`;

export const depositInvoiceLine = (deposit: DepositInvoiceSource): Line => {
  const amount = Number(deposit.amount);
  return {
    id: `deposit-${deposit.id}`,
    name: depositInvoiceLineName(deposit),
    unit: "бр.",
    vatRate: deposit.vatRate ?? DEPOSIT_INVOICE_VAT_RATE,
    quantity: 1,
    unitPrice: amount,
    total: amount,
  };
};

/** Общата стойност на фактурата за депозит трябва да е сумата на депозита
 *  (BE `ensure_deposit_invoiceable` отказва иначе) — редът се сменя, сумата не. */
export const isDepositInvoiceTotalMismatch = (
  deposit: DepositInvoiceSource,
  grandTotal: number,
): boolean => Math.round(grandTotal * 100) !== (parseMoneyToCents(deposit.amount) ?? 0);

/** Методът на депозита, веднъж за отваряне — ако е сред методите на
 *  фактурата (иначе остава изборът по подразбиране). Опреснените методи не
 *  връщат ръчно сменения. */
export function useDepositInvoiceMethod({
  open,
  deposit,
  paymentOptions,
  setPaymentMethodId,
}: {
  open: boolean;
  deposit: DepositInvoiceSource | null;
  paymentOptions: ActivePaymentMethod[];
  setPaymentMethodId: (paymentMethodId: number) => void;
}) {
  const appliedDepositIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) {
      appliedDepositIdRef.current = null;
      return;
    }
    if (!deposit || appliedDepositIdRef.current === deposit.id) return;
    if (!paymentOptions.some((method) => method.id === deposit.paymentMethodId)) return;
    appliedDepositIdRef.current = deposit.id;
    setPaymentMethodId(deposit.paymentMethodId);
  }, [open, deposit, paymentOptions, setPaymentMethodId]);
}
